import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Alert, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  StyledModal,
  StyledModalHeader,
  MainButton,
} from '../../../../common/form/FormElements.jsx';
import FormButtonsContainer from '../../../../antd/form/FormButtonsContainer/FormButtonsContainer.jsx';
import FormSelect from '../../../../antd/form/FormSelect/FormSelect.jsx';
import FormSwitch from '../../../../antd/form/FormSwitch/FormSwitch.jsx';
import { required } from '../../../../antd/form/FormValidation.js';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { useGetIngProjectsQuery } from '../../../../../redux/services/ing_projects.jsx';
import { useGetStudyProgramsForIngProjectsQuery } from '../../../../../redux/services/study_programs.jsx';
import {
  useCreateChangeRequestMutation,
  useGetChangeRequestsQuery,
} from '../../../../../redux/services/ing_projects_change_requests.jsx';

const IpChangeRequestCreateModal = ({
  visible,
  onClose,
  currentProjectId,
  currentStudyProgramId,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { apiErrorMessage, successMessage, errorMessage } = useToastMessages();
  const [isDifferentProgram, setIsDifferentProgram] = useState(false);
  const [selectedStudyProgramId, setSelectedStudyProgramId] = useState(null);

  const { data: allProjects } = useGetIngProjectsQuery();
  const { data: studyPrograms } = useGetStudyProgramsForIngProjectsQuery(
    undefined,
    { skip: !isDifferentProgram },
  );
  const { data: changeRequests } = useGetChangeRequestsQuery(undefined, {
    skip: !visible,
  });
  const [createChangeRequest, { isLoading: isCreating }] =
    useCreateChangeRequestMutation();

  const hasPendingChangeRequest = useMemo(
    () => Array.isArray(changeRequests) && changeRequests.some((request) => request.state === 'Čaká na schválenie' || request.state === 0),
    [changeRequests],
  );

  // Filter active, approved projects excluding the current one
  const availableProjects = useMemo(() => {
    if (!allProjects) return [];
    return allProjects.filter(
      (project) =>
        project.id !== currentProjectId &&
        project.archived_at == null &&
        project.active === 'Áno' &&
        project.study_programs?.every((sp) => sp.pivot?.approved === 'Áno'),
    );
  }, [allProjects, currentProjectId]);

  // Filter projects by the target study program
  const targetStudyProgramId = isDifferentProgram
    ? selectedStudyProgramId
    : currentStudyProgramId;

  const projectOptions = useMemo(() => {
    if (!targetStudyProgramId) return [];
    return availableProjects
      .filter((p) =>
        p.study_programs?.some((sp) => sp.id === targetStudyProgramId),
      )
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sk'))
      .map((p) => ({
        value: p.id,
        label: p.name,
      }));
  }, [availableProjects, targetStudyProgramId]);

  // Study program options (exclude current study program)
  const studyProgramOptions = useMemo(() => {
    if (!studyPrograms) return [];
    return studyPrograms
      .filter((sp) => sp.id !== currentStudyProgramId)
      .map((sp) => ({
        value: sp.id,
        label: sp.name,
      }));
  }, [studyPrograms, currentStudyProgramId]);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setIsDifferentProgram(false);
      setSelectedStudyProgramId(null);
    }
  }, [visible, form]);

  const handleDifferentProgramChange = (checked) => {
    setIsDifferentProgram(checked);
    setSelectedStudyProgramId(null);
    form.setFieldValue('study_program_id', undefined);
    form.setFieldValue('new_project_id', undefined);
  };

  const handleStudyProgramChange = (value) => {
    setSelectedStudyProgramId(value);
    form.setFieldValue('new_project_id', undefined);
  };

  const handleFinish = async (values) => {
    if (hasPendingChangeRequest) {
      return;
    }

    if (!isDifferentProgram && !currentStudyProgramId) {
      errorMessage({
        message: t('Nemáte priradený žiadny zverejnený projekt.'),
      });
      return;
    }

    try {
      await createChangeRequest({
        new_project_id: values.new_project_id,
        study_program_id: isDifferentProgram
          ? values.study_program_id
          : currentStudyProgramId,
      }).unwrap();

      successMessage({
        message: t('Žiadosť o zmenu projektu bola úspešne odoslaná.'),
      });
      form.resetFields();
      onClose();
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIsDifferentProgram(false);
    setSelectedStudyProgramId(null);
    onClose();
  };

  return (
    <StyledModal open={visible} size="md" onCancel={handleCancel} footer={null}>
      <StyledModalHeader>{t('Žiadosť o zmenu projektu')}</StyledModalHeader>
      <div style={{ padding: '10px' }}>
        <Form form={form} onFinish={handleFinish} layout="vertical">
          {hasPendingChangeRequest && (
            <Alert
              type="warning"
              showIcon
              message={t('Vaša žiadosť o zmenu projektu sa spracováva. O výsledku spracovania budete informovaný e-mailom.')}
              style={{ marginBottom: 16 }}
            />
          )}
          <FormSwitch
            label={t('Projekt je z iného študijného programu')}
            name="is_different_program"
            onChange={handleDifferentProgramChange}
            disabled={hasPendingChangeRequest}
          />

          {isDifferentProgram && (
            <FormSelect
              name="study_program_id"
              label={t('Študijný program')}
              options={studyProgramOptions}
              rules={[required(t)]}
              placeholder={t('Vyberte študijný program')}
              onChange={handleStudyProgramChange}
              disabled={hasPendingChangeRequest}
              showSearch
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            />
          )}

          <FormSelect
            name="new_project_id"
            label={t('Nový projekt')}
            options={projectOptions}
            rules={[required(t)]}
            placeholder={t('Vyberte nový projekt')}
            disabled={hasPendingChangeRequest || (isDifferentProgram && !selectedStudyProgramId)}
            showSearch
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />

          <FormButtonsContainer>
            <MainButton onClick={handleCancel}>{t('Zrušiť')}</MainButton>
            <MainButton htmlType="submit" loading={isCreating} disabled={hasPendingChangeRequest}>
              {t('Odoslať žiadosť')}
            </MainButton>
          </FormButtonsContainer>
        </Form>
      </div>
    </StyledModal>
  );
};

IpChangeRequestCreateModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentProjectId: PropTypes.number,
  currentStudyProgramId: PropTypes.number,
};

export default IpChangeRequestCreateModal;
